// src/pages/document/DocumentAdminPage.tsx
// Records-system configuration hub (admin-gated): policies, numbering (advanced
// counter/reset/preview ops), registration journals, and approval-pipeline
// definitions. Document types and categories keep their own pages, linked here.

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useDocument, useI18n } from '@react/hooks';
import { useScope } from '@/hooks/useScope';
import { Badge, ErrorAlert } from '@react/components';
import { Select } from '@/components/Select';
import { FormRow } from '@/components/form';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { formatDate } from '@/features/document/logic';
import {
  registrationDirectionKeys,
  registrationDirectionLabelKey,
  pipelineActionMap,
  mapping,
} from '@/features/document/complianceLogic';

type AdminTab = 'policies' | 'numbering' | 'registers' | 'pipelines';

const TABS: { id: AdminTab; labelKey: string }[] = [
  { id: 'policies', labelKey: 'document.admin.tab.policies' },
  { id: 'numbering', labelKey: 'document.admin.tab.numbering' },
  { id: 'registers', labelKey: 'document.admin.tab.registers' },
  { id: 'pipelines', labelKey: 'document.admin.tab.pipelines' },
];

const inputCls = 'w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-ring';

export const DocumentAdminPage = observer(() => {
  const { t } = useI18n();
  const [tab, setTab] = useState<AdminTab>('policies');

  return (
    <div className="max-w-[1100px] mx-auto px-6 py-6">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-foreground">{t('document.admin.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('document.admin.subtitle')}</p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-5 text-sm">
        <Link to="/documents/types" className="px-3 py-1.5 rounded-md border border-border text-foreground hover:bg-muted transition-colors">{t('document.admin.manageTypes')}</Link>
        <Link to="/documents/categories" className="px-3 py-1.5 rounded-md border border-border text-foreground hover:bg-muted transition-colors">{t('document.admin.manageCategories')}</Link>
        <Link to="/documents/numbering-schemes/new" className="px-3 py-1.5 rounded-md border border-border text-foreground hover:bg-muted transition-colors">{t('document.admin.newScheme')}</Link>
      </div>

      <div className="flex gap-1 border-b border-border mb-5" role="tablist" aria-label={t('document.admin.title')}>
        {TABS.map((tb) => (
          <button
            key={tb.id}
            type="button"
            role="tab"
            aria-selected={tab === tb.id}
            onClick={() => setTab(tb.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === tb.id ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            {t(tb.labelKey)}
          </button>
        ))}
      </div>

      {tab === 'policies' && <PoliciesTab />}
      {tab === 'numbering' && <NumberingTab />}
      {tab === 'registers' && <RegistersTab />}
      {tab === 'pipelines' && <PipelinesTab />}
    </div>
  );
});

// ---------------------------------------------------------------------------
// Policies
// ---------------------------------------------------------------------------

const PoliciesTab = observer(() => {
  const { t } = useI18n();
  const store = useDocument();
  const scope = useScope();
  const [policies, setPolicies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [selected, setSelected] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [resolveType, setResolveType] = useState('');
  const [resolveId, setResolveId] = useState('');
  const [resolved, setResolved] = useState<any>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    store.ListDocumentPolicies()
      .then((res: any) => setPolicies(res?.policies?.policies ?? []))
      .catch((e: unknown) => setError(e))
      .finally(() => setLoading(false));
  }, [scope.productId, store]);

  useEffect(() => { load(); }, [load]);

  const openPolicy = (p: any) => {
    store.GetDocumentPolicy(p.entity.id).then((res: any) => setSelected(res?.policy ?? res)).catch(() => setSelected(p));
  };

  const remove = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await store.DeleteDocumentPolicy(selected.entity.id);
      setSelected(null);
      setConfirmDelete(false);
      load();
    } catch { /* store */ } finally { setBusy(false); }
  };

  const resolve = async () => {
    if (!resolveType.trim() || !resolveId.trim()) return;
    setBusy(true);
    try {
      const res: any = await store.ResolveDocumentContext({
        scope_entity: { type_name: resolveType.trim(), id: resolveId.trim(), product_id: scope.productId },
        scope_chain: [],
      } as any);
      setResolved(res);
    } catch { setResolved(null); } finally { setBusy(false); }
  };

  const lookupByEntity = async () => {
    if (!resolveType.trim() || !resolveId.trim()) return;
    setBusy(true);
    try {
      const res: any = await store.GetDocumentPolicyByEntity(resolveType.trim(), resolveId.trim());
      setSelected(res?.policy ?? res);
    } catch { /* store */ } finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-5">
      <div>
        {error ? <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} /> : null}
        {loading ? (
          <div className="space-y-2 animate-pulse" aria-busy="true">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-14 rounded bg-muted" />)}</div>
        ) : policies.length === 0 ? (
          <p className="text-sm text-muted-foreground py-10 text-center">{t('document.admin.policies.empty')}</p>
        ) : (
          <ul className="space-y-1.5">
            {policies.map((p) => (
              <li key={p.entity?.id}>
                <button type="button" onClick={() => openPolicy(p)} className={`w-full text-left rounded-lg border p-3 transition-colors ${selected?.entity?.id === p.entity?.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground truncate flex-1">{p.name || p.scope_entity_type}</span>
                    {p.is_active ? <Badge variant="success" size="sm">{t('document.admin.active')}</Badge> : <Badge variant="secondary" size="sm">{t('document.admin.inactive')}</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{p.scope_entity_type}{p.require_approval ? ` · ${t('document.admin.policies.approval')}` : ''}</div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="space-y-4">
        {selected && (
          <div className="rounded-lg border border-border p-4 space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-foreground">{selected.name || selected.scope_entity_type}</h3>
              <button type="button" onClick={() => setConfirmDelete(true)} className="text-destructive hover:underline text-xs">{t('common.delete')}</button>
            </div>
            <Row label={t('document.admin.policies.scopeType')} value={selected.scope_entity_type} />
            <Row label={t('document.admin.policies.approval')} value={selected.require_approval ? t('common.yes') : t('common.no')} />
            <Row label={t('document.admin.policies.acknowledgment')} value={selected.require_acknowledgment ? t('common.yes') : t('common.no')} />
            <Row label={t('document.admin.policies.versioning')} value={selected.enable_versioning ? t('common.yes') : t('common.no')} />
            <Row label={t('document.admin.policies.registration')} value={selected.enable_registration ? t('common.yes') : t('common.no')} />
            <Row label={t('document.admin.policies.allowedTypes')} value={String((selected.allowed_document_type_ids || []).length)} />
          </div>
        )}

        <div className="rounded-lg border border-border p-4 space-y-2.5">
          <h3 className="text-sm font-semibold text-foreground">{t('document.admin.policies.resolver')}</h3>
          <p className="text-xs text-muted-foreground">{t('document.admin.policies.resolverHint')}</p>
          <input className={inputCls} value={resolveType} onChange={(e) => setResolveType(e.target.value)} placeholder={t('document.admin.policies.entityType')} aria-label={t('document.admin.policies.entityType')} />
          <input className={inputCls} value={resolveId} onChange={(e) => setResolveId(e.target.value)} placeholder={t('document.admin.policies.entityId')} aria-label={t('document.admin.policies.entityId')} />
          <div className="flex gap-2">
            <button type="button" disabled={busy} onClick={resolve} className="flex-1 px-3 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 transition-colors">{t('document.admin.policies.resolve')}</button>
            <button type="button" disabled={busy} onClick={lookupByEntity} className="flex-1 px-3 py-2 text-sm font-medium rounded-md border border-border text-foreground hover:bg-muted disabled:opacity-40 transition-colors">{t('document.admin.policies.direct')}</button>
          </div>
          {resolved && (
            <div className="rounded-md bg-muted/40 p-2.5 text-xs space-y-1">
              <Row label={t('document.admin.policies.effective')} value={resolved.effective_policy?.name || resolved.effective_policy?.scope_entity_type || t('common.none')} />
              <Row label={t('document.admin.policies.chain')} value={String((resolved.policy_chain || []).length)} />
              <Row label={t('document.admin.policies.templates')} value={String((resolved.available_templates || []).length)} />
              <Row label={t('document.admin.policies.schemes')} value={String((resolved.available_numbering_schemes || []).length)} />
              <Row label={t('document.admin.policies.registers')} value={String((resolved.available_registers || []).length)} />
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog open={confirmDelete} title={t('document.admin.policies.deleteTitle')} description={t('document.admin.policies.deleteBody')} confirmLabel={t('common.delete')} destructive isConfirming={busy} onConfirm={remove} onCancel={() => setConfirmDelete(false)} />
    </div>
  );
});

// ---------------------------------------------------------------------------
// Numbering (advanced)
// ---------------------------------------------------------------------------

const NumberingTab = observer(() => {
  const { t, locale } = useI18n();
  const store = useDocument();
  const scope = useScope();
  const [schemes, setSchemes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [selected, setSelected] = useState<any>(null);
  const [counters, setCounters] = useState<any[]>([]);
  const [preview, setPreview] = useState<string>('');
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [resetKey, setResetKey] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    store.ListNumberingSchemes()
      .then((res: any) => setSchemes(res?.schemes?.schemes ?? []))
      .catch((e: unknown) => setError(e))
      .finally(() => setLoading(false));
  }, [scope.productId, store]);

  useEffect(() => { load(); }, [load]);

  const openScheme = (s: any) => {
    store.GetNumberingScheme(s.entity.id).then((res: any) => setSelected(res?.scheme ?? res)).catch(() => setSelected(s));
    setPreview('');
    store.ListNumberingCounters(s.entity.id).then((res: any) => setCounters(res?.counters?.counters ?? [])).catch(() => setCounters([]));
  };

  const doPreview = async () => {
    if (!selected) return;
    setBusy('preview');
    try {
      const res: any = await store.PreviewDocumentNumber(selected.entity.id, { scheme: selected.entity } as any);
      setPreview(res?.document_number ?? '');
    } catch { /* store */ } finally { setBusy(null); }
  };

  const toggleActive = async () => {
    if (!selected) return;
    setBusy('toggle');
    try {
      await store.UpdateNumberingScheme(selected.entity.id, { scheme: { ...selected, is_active: !selected.is_active } } as any);
      setSelected({ ...selected, is_active: !selected.is_active });
      load();
    } catch { /* store */ } finally { setBusy(null); }
  };

  const resetCounter = async () => {
    if (!selected) return;
    setBusy('reset');
    try {
      await store.ResetNumberingCounter(selected.entity.id, { scheme: selected.entity, scope_key: resetKey.trim() || undefined, reset_to: 0 } as any);
      setResetKey('');
      store.ListNumberingCounters(selected.entity.id).then((res: any) => setCounters(res?.counters?.counters ?? [])).catch(() => {});
    } catch { /* store */ } finally { setBusy(null); }
  };

  const remove = async () => {
    if (!selected) return;
    setBusy('delete');
    try {
      await store.DeleteNumberingScheme(selected.entity.id);
      setSelected(null);
      setConfirmDelete(false);
      load();
    } catch { /* store */ } finally { setBusy(null); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-5">
      <div>
        {error ? <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} /> : null}
        {loading ? (
          <div className="space-y-2 animate-pulse" aria-busy="true">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-14 rounded bg-muted" />)}</div>
        ) : schemes.length === 0 ? (
          <div className="py-10 text-center">
            <p className="text-sm text-muted-foreground mb-3">{t('document.admin.numbering.empty')}</p>
            <Link to="/documents/numbering-schemes/new" className="text-sm text-primary hover:underline">{t('document.admin.newScheme')}</Link>
          </div>
        ) : (
          <ul className="space-y-1.5">
            {schemes.map((s) => (
              <li key={s.entity?.id}>
                <button type="button" onClick={() => openScheme(s)} className={`w-full text-left rounded-lg border p-3 transition-colors ${selected?.entity?.id === s.entity?.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground truncate flex-1">{s.name}</span>
                    <span className="font-mono text-xs text-muted-foreground">{s.prefix}{s.separator}#</span>
                    {!s.is_active && <Badge variant="secondary" size="sm">{t('document.admin.inactive')}</Badge>}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {selected && (
        <div className="space-y-4">
          <div className="rounded-lg border border-border p-4 space-y-2.5">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">{selected.name}</h3>
              <Badge variant={selected.is_active ? 'success' : 'secondary'} size="sm">{selected.is_active ? t('document.admin.active') : t('document.admin.inactive')}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" disabled={busy !== null} onClick={doPreview} className="px-3 py-1.5 text-sm rounded-md border border-border text-foreground hover:bg-muted disabled:opacity-40 transition-colors">{t('document.admin.numbering.preview')}</button>
              {preview && <span className="font-mono text-sm text-foreground">{preview}</span>}
            </div>
            <div className="flex items-center gap-2">
              <button type="button" disabled={busy !== null} onClick={toggleActive} className="px-3 py-1.5 text-sm rounded-md border border-border text-foreground hover:bg-muted disabled:opacity-40 transition-colors">{selected.is_active ? t('document.admin.numbering.deactivate') : t('document.admin.numbering.activate')}</button>
              <button type="button" onClick={() => setConfirmDelete(true)} className="px-3 py-1.5 text-sm rounded-md text-destructive hover:bg-destructive/10 transition-colors">{t('common.delete')}</button>
            </div>
          </div>

          <div className="rounded-lg border border-border p-4 space-y-2.5">
            <h3 className="text-sm font-semibold text-foreground">{t('document.admin.numbering.counters')}</h3>
            {counters.length === 0 ? (
              <p className="text-xs text-muted-foreground">{t('document.admin.numbering.noCounters')}</p>
            ) : (
              <ul className="space-y-1">
                {counters.map((c, i) => (
                  <li key={c.entity?.id || i} className="flex items-center gap-2 text-xs">
                    <span className="font-mono text-foreground truncate flex-1">{c.scope_key || t('document.admin.numbering.globalScope')}</span>
                    <span className="text-muted-foreground">{c.current_value}</span>
                    <span className="text-[11px] text-muted-foreground">{formatDate(c.last_incremented_at, locale)}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex items-center gap-2 pt-1">
              <input className={inputCls} value={resetKey} onChange={(e) => setResetKey(e.target.value)} placeholder={t('document.admin.numbering.scopeKey')} aria-label={t('document.admin.numbering.scopeKey')} />
              <button type="button" disabled={busy !== null} onClick={resetCounter} className="px-3 py-1.5 text-sm rounded-md border border-destructive text-destructive hover:bg-destructive/10 disabled:opacity-40 transition-colors whitespace-nowrap">{t('document.admin.numbering.reset')}</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog open={confirmDelete} title={t('document.admin.numbering.deleteTitle')} description={t('document.admin.numbering.deleteBody')} confirmLabel={t('common.delete')} destructive isConfirming={busy === 'delete'} onConfirm={remove} onCancel={() => setConfirmDelete(false)} />
    </div>
  );
});

// ---------------------------------------------------------------------------
// Registers
// ---------------------------------------------------------------------------

const RegistersTab = observer(() => {
  const { t, locale } = useI18n();
  const store = useDocument();
  const scope = useScope();
  const [registers, setRegisters] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [selected, setSelected] = useState<any>(null);
  const [registrations, setRegistrations] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [direction, setDirection] = useState('DOCUMENT_REGISTRATION_DIRECTION_INTERNAL');
  const [prefix, setPrefix] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    store.ListDocumentRegisters()
      .then((res: any) => setRegisters(res?.registers?.registers ?? []))
      .catch((e: unknown) => setError(e))
      .finally(() => setLoading(false));
  }, [scope.productId, store]);

  useEffect(() => { load(); }, [load]);

  const openRegister = (r: any) => {
    setSelected(r);
    store.ListDocumentRegistrations(r.entity.id).then((res: any) => setRegistrations(res?.registrations?.registrations ?? [])).catch(() => setRegistrations([]));
  };

  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await store.CreateDocumentRegister({
        entity: { product_id: scope.productId, type_name: 'document_register' },
        name,
        direction,
        prefix: prefix.trim() || undefined,
      } as any);
      setCreating(false); setName(''); setPrefix('');
      load();
    } catch { /* store */ } finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-5">
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-foreground">{t('document.admin.registers.title')}</h3>
          <button type="button" onClick={() => setCreating((v) => !v)} className="text-sm text-primary hover:underline">{t('document.admin.registers.new')}</button>
        </div>
        {creating && (
          <div className="rounded-lg border border-border p-3 mb-3">
            <FormRow label={t('document.admin.registers.name')} labelWidth="sm" htmlFor="register-name">
              <input id="register-name" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder={t('document.admin.registers.name')} />
            </FormRow>
            <FormRow label={t('document.admin.registers.direction')} labelWidth="sm">
              <Select value={direction} onChange={(e: any) => setDirection(e.target.value)} aria-label={t('document.admin.registers.direction')} fullWidth={false} className="w-56">
                {registrationDirectionKeys.map((k) => <option key={k} value={k}>{t(registrationDirectionLabelKey(k))}</option>)}
              </Select>
            </FormRow>
            <FormRow label={t('document.admin.registers.prefix')} labelWidth="sm" htmlFor="register-prefix">
              <input id="register-prefix" className={inputCls} value={prefix} onChange={(e) => setPrefix(e.target.value)} placeholder={t('document.admin.registers.prefix')} />
            </FormRow>
            <button type="button" disabled={busy || !name.trim()} onClick={create} className="w-full mt-1 px-3 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 transition-colors">{busy ? t('common.saving') : t('common.create')}</button>
          </div>
        )}
        {error ? <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} /> : null}
        {loading ? (
          <div className="space-y-2 animate-pulse" aria-busy="true">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-14 rounded bg-muted" />)}</div>
        ) : registers.length === 0 ? (
          <p className="text-sm text-muted-foreground py-10 text-center">{t('document.admin.registers.empty')}</p>
        ) : (
          <ul className="space-y-1.5">
            {registers.map((r) => (
              <li key={r.entity?.id}>
                <button type="button" onClick={() => openRegister(r)} className={`w-full text-left rounded-lg border p-3 transition-colors ${selected?.entity?.id === r.entity?.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground truncate flex-1">{r.name}</span>
                    <Badge variant="default" size="sm">{t(registrationDirectionLabelKey(r.direction))}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">{t('document.admin.registers.count', { n: r.registration_count ?? 0 })}</div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {selected && (
        <div className="rounded-lg border border-border p-4">
          <h3 className="text-sm font-semibold text-foreground mb-2">{selected.name}</h3>
          {registrations.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t('document.admin.registers.noEntries')}</p>
          ) : (
            <ul className="space-y-1">
              {registrations.slice(0, 20).map((r, i) => (
                <li key={r.entity?.id || i} className="flex items-center gap-2 text-xs border-b border-border/50 py-1">
                  <span className="font-mono text-foreground">{r.registration_number}</span>
                  <span className="text-muted-foreground truncate flex-1">{r.summary}</span>
                  <span className="text-muted-foreground">{formatDate(r.registration_date, locale)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
});

// ---------------------------------------------------------------------------
// Approval pipeline definitions
// ---------------------------------------------------------------------------

const PipelinesTab = observer(() => {
  const { t } = useI18n();
  const store = useDocument();
  const scope = useScope();
  const [pipelines, setPipelines] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [selected, setSelected] = useState<any>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [stages, setStages] = useState<{ name: string; action: string }[]>([{ name: '', action: 'DOCUMENT_PIPELINE_ACTION_APPROVE' }]);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    store.ListPipelines()
      .then((res: any) => setPipelines(res?.pipelines?.pipelines ?? []))
      .catch((e: unknown) => setError(e))
      .finally(() => setLoading(false));
  }, [scope.productId, store]);

  useEffect(() => { load(); }, [load]);

  const openPipeline = (p: any) => {
    store.GetPipeline(p.entity.id).then((res: any) => setSelected(res?.pipeline ?? res)).catch(() => setSelected(p));
  };

  const buildStages = () => stages.filter((s) => s.name.trim()).map((s, i) => ({
    id: '', pipeline_id: '', name: s.name, description: '', order: i, actors: [],
    action: s.action, completion_rule: 'PIPELINE_COMPLETION_RULE_ALL',
    auto_skip_on_timeout: false, allow_delegation: true, require_comment: false, notify_on_entry: true,
  }));

  const create = async () => {
    if (!name.trim()) return;
    setBusy('create');
    try {
      await store.CreatePipeline({
        entity: { product_id: scope.productId, type_name: 'document_pipeline' },
        name,
        stages: buildStages(),
      } as any);
      setCreating(false); setName(''); setStages([{ name: '', action: 'DOCUMENT_PIPELINE_ACTION_APPROVE' }]);
      load();
    } catch { /* store */ } finally { setBusy(null); }
  };

  const activate = async (p: any) => {
    setBusy('activate');
    try {
      await store.UpdatePipeline(p.entity.id, { pipeline: { ...p, status: 'DOCUMENT_PIPELINE_STATUS_ACTIVE' } } as any);
      load();
      setSelected({ ...p, status: 'DOCUMENT_PIPELINE_STATUS_ACTIVE' });
    } catch { /* store */ } finally { setBusy(null); }
  };

  const remove = async () => {
    if (!selected) return;
    setBusy('delete');
    try {
      await store.DeletePipeline(selected.entity.id);
      setSelected(null); setConfirmDelete(false); load();
    } catch { /* store */ } finally { setBusy(null); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-5">
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-foreground">{t('document.admin.pipelines.title')}</h3>
          <button type="button" onClick={() => setCreating((v) => !v)} className="text-sm text-primary hover:underline">{t('document.admin.pipelines.new')}</button>
        </div>

        {creating && (
          <div className="rounded-lg border border-border p-3 mb-3">
            <FormRow label={t('document.admin.pipelines.name')} labelWidth="sm" htmlFor="pipeline-name">
              <input id="pipeline-name" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder={t('document.admin.pipelines.name')} />
            </FormRow>
            <FormRow label={t('document.admin.pipelines.stages')} labelWidth="sm">
              <div className="space-y-2">
                {stages.map((s, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input className={inputCls} value={s.name} onChange={(e) => setStages(stages.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} placeholder={t('document.admin.pipelines.stageName')} aria-label={t('document.admin.pipelines.stageName')} />
                    <Select value={s.action} onChange={(e: any) => setStages(stages.map((x, j) => j === i ? { ...x, action: e.target.value } : x))} aria-label={t('document.admin.pipelines.stageAction')} fullWidth={false} className="w-48">
                      {Object.keys(pipelineActionMap).map((k) => <option key={k} value={k}>{t(pipelineActionMap[k].labelKey)}</option>)}
                    </Select>
                    {stages.length > 1 && (
                      <button type="button" onClick={() => setStages(stages.filter((_, j) => j !== i))} aria-label={t('document.admin.pipelines.removeStage')} className="p-1.5 rounded-md text-destructive hover:bg-destructive/10">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={() => setStages([...stages, { name: '', action: 'DOCUMENT_PIPELINE_ACTION_APPROVE' }])} className="text-xs text-primary hover:underline">{t('document.admin.pipelines.addStage')}</button>
              </div>
            </FormRow>
            <button type="button" disabled={busy !== null || !name.trim()} onClick={create} className="w-full mt-1 px-3 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 transition-colors">{busy === 'create' ? t('common.saving') : t('common.create')}</button>
          </div>
        )}

        {error ? <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} /> : null}
        {loading ? (
          <div className="space-y-2 animate-pulse" aria-busy="true">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-14 rounded bg-muted" />)}</div>
        ) : pipelines.length === 0 ? (
          <p className="text-sm text-muted-foreground py-10 text-center">{t('document.admin.pipelines.empty')}</p>
        ) : (
          <ul className="space-y-1.5">
            {pipelines.map((p) => (
              <li key={p.entity?.id}>
                <button type="button" onClick={() => openPipeline(p)} className={`w-full text-left rounded-lg border p-3 transition-colors ${selected?.entity?.id === p.entity?.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground truncate flex-1">{p.name}</span>
                    <span className="text-xs text-muted-foreground">{t('document.admin.pipelines.stageCount', { n: (p.stages || []).length })}</span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {selected && (
        <div className="rounded-lg border border-border p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground">{selected.name}</h3>
            <Badge variant={selected.status === 'DOCUMENT_PIPELINE_STATUS_ACTIVE' ? 'success' : 'secondary'} size="sm">
              {selected.status === 'DOCUMENT_PIPELINE_STATUS_ACTIVE' ? t('document.admin.active') : t('document.admin.pipelines.draft')}
            </Badge>
          </div>
          <ol className="space-y-1.5">
            {(selected.stages || []).map((s: any, i: number) => (
              <li key={s.id || i} className="flex items-center gap-2 rounded-md border border-border p-2">
                <span className="text-xs font-medium text-foreground truncate flex-1">{i + 1}. {s.name}</span>
                {s.action && <Badge variant={mapping(pipelineActionMap, s.action).variant as any} size="sm">{t(mapping(pipelineActionMap, s.action).labelKey)}</Badge>}
              </li>
            ))}
          </ol>
          <div className="flex gap-2">
            {selected.status !== 'DOCUMENT_PIPELINE_STATUS_ACTIVE' && (
              <button type="button" disabled={busy !== null} onClick={() => activate(selected)} className="px-3 py-1.5 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 transition-colors">{t('document.admin.pipelines.activate')}</button>
            )}
            <button type="button" onClick={() => setConfirmDelete(true)} className="px-3 py-1.5 text-sm rounded-md text-destructive hover:bg-destructive/10 transition-colors">{t('common.delete')}</button>
          </div>
        </div>
      )}

      <ConfirmDialog open={confirmDelete} title={t('document.admin.pipelines.deleteTitle')} description={t('document.admin.pipelines.deleteBody')} confirmLabel={t('common.delete')} destructive isConfirming={busy === 'delete'} onConfirm={remove} onCancel={() => setConfirmDelete(false)} />
    </div>
  );
});

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-muted-foreground w-[130px] flex-shrink-0">{label}</span>
      <span className="text-foreground truncate">{value}</span>
    </div>
  );
}

export default DocumentAdminPage;
